/**
 * Order of Mass (Ordo Missae)
 *
 * The fixed parts of the Roman Rite Mass, Third Typical Edition (2010/2011).
 *
 * Excerpts from the English translation of The Roman Missal © 2010,
 * International Commission on English in the Liturgy Corporation (ICEL).
 * All rights reserved.
 *
 * Reproduced on a non-commercial, free-access basis in accordance with
 * ICEL publication policies for non-commercial Internet sites.
 */

export interface MassSection {
  id: string;
  title: string;
  rubric?: string;
  parts: MassPart[];
}

export interface MassPart {
  speaker?: 'priest' | 'people' | 'deacon' | 'all' | 'rubric' | 'lector';
  text: string;
  latin?: string;
  isResponse?: boolean;
}

export const INTRODUCTORY_RITES: MassSection[] = [
  {
    id: 'entrance',
    title: 'Entrance',
    rubric: 'When the people are gathered, the Priest approaches the altar with the ministers while the Entrance Chant is sung.',
    parts: [],
  },
  {
    id: 'greeting',
    title: 'Greeting',
    parts: [
      { speaker: 'rubric', text: 'After the sign of the Cross:' },
      { speaker: 'priest', text: 'In the name of the Father, and of the Son, and of the Holy Spirit.' },
      { speaker: 'people', text: 'Amen.', isResponse: true },
      { speaker: 'rubric', text: 'The Priest greets the people, using one of the following:' },
      { speaker: 'priest', text: 'The grace of our Lord Jesus Christ,\nand the love of God,\nand the communion of the Holy Spirit\nbe with you all.' },
      { speaker: 'people', text: 'And with your spirit.', isResponse: true },
      { speaker: 'rubric', text: 'Or:' },
      { speaker: 'priest', text: 'Grace to you and peace from God our Father\nand the Lord Jesus Christ.' },
      { speaker: 'people', text: 'And with your spirit.', isResponse: true },
      { speaker: 'rubric', text: 'Or:' },
      { speaker: 'priest', text: 'The Lord be with you.' },
      { speaker: 'people', text: 'And with your spirit.', isResponse: true },
    ],
  },
  {
    id: 'penitential-act',
    title: 'Penitential Act',
    parts: [
      { speaker: 'rubric', text: 'The Priest invites the faithful to the Penitential Act:' },
      { speaker: 'priest', text: 'Brethren (brothers and sisters),\nlet us acknowledge our sins,\nand so prepare ourselves to celebrate the sacred mysteries.' },
      { speaker: 'rubric', text: 'A brief pause for silence follows. Then one of the following forms is used:' },
      { speaker: 'rubric', text: 'FORM I (Confiteor):' },
      { speaker: 'all', text: 'I confess to almighty God\nand to you, my brothers and sisters,\nthat I have greatly sinned,\nin my thoughts and in my words,\nin what I have done and in what I have failed to do,\nthrough my fault, through my fault,\nthrough my most grievous fault;\ntherefore I ask blessed Mary ever-Virgin,\nall the Angels and Saints,\nand you, my brothers and sisters,\nto pray for me to the Lord our God.' },
      { speaker: 'priest', text: 'May almighty God have mercy on us,\nforgive us our sins,\nand bring us to everlasting life.' },
      { speaker: 'people', text: 'Amen.', isResponse: true },
    ],
  },
  {
    id: 'kyrie',
    title: 'Kyrie',
    parts: [
      { speaker: 'rubric', text: 'Unless it has occurred in the Penitential Act:' },
      { speaker: 'priest', text: 'Lord, have mercy.', latin: 'Kýrie, eléison.' },
      { speaker: 'people', text: 'Lord, have mercy.', latin: 'Kýrie, eléison.', isResponse: true },
      { speaker: 'priest', text: 'Christ, have mercy.', latin: 'Christe, eléison.' },
      { speaker: 'people', text: 'Christ, have mercy.', latin: 'Christe, eléison.', isResponse: true },
      { speaker: 'priest', text: 'Lord, have mercy.', latin: 'Kýrie, eléison.' },
      { speaker: 'people', text: 'Lord, have mercy.', latin: 'Kýrie, eléison.', isResponse: true },
    ],
  },
  {
    id: 'gloria',
    title: 'Gloria',
    rubric: 'The Gloria is sung or said on Sundays (outside of Advent and Lent), Solemnities, and Feasts.',
    parts: [
      {
        speaker: 'all',
        text: 'Glory to God in the highest,\nand on earth peace to people of good will.\n\nWe praise you,\nwe bless you,\nwe adore you,\nwe glorify you,\nwe give you thanks for your great glory,\nLord God, heavenly King,\nO God, almighty Father.\n\nLord Jesus Christ, Only Begotten Son,\nLord God, Lamb of God, Son of the Father,\nyou take away the sins of the world,\n\thave mercy on us;\nyou take away the sins of the world,\n\treceive our prayer;\nyou are seated at the right hand of the Father,\n\thave mercy on us.\n\nFor you alone are the Holy One,\nyou alone are the Lord,\nyou alone are the Most High,\nJesus Christ,\nwith the Holy Spirit,\nin the glory of God the Father.\nAmen.',
        latin: 'Glória in excélsis Deo\net in terra pax homínibus bonae voluntátis.\nLaudámus te,\nbenedícimus te,\nadorámus te,\nglorificámus te,\ngrátias ágimus tibi propter magnam glóriam tuam,\nDómine Deus, Rex cæléstis,\nDeus Pater omnípotens.\nDómine Fili Unigénite, Iesu Christe,\nDómine Deus, Agnus Dei, Fílius Patris,\nqui tollis peccáta mundi,\n\tmiserére nobis;\nqui tollis peccáta mundi,\n\tsúscipe deprecatiónem nostram.\nQui sedes ad déxteram Patris,\n\tmiserére nobis.\nQuóniam tu solus Sanctus,\ntu solus Dóminus,\ntu solus Altíssimus,\nIesu Christe,\ncum Sancto Spíritu:\nin glória Dei Patris.\nAmen.',
      },
    ],
  },
  {
    id: 'collect',
    title: 'Collect',
    parts: [
      { speaker: 'priest', text: 'Let us pray.' },
      { speaker: 'rubric', text: 'All pray in silence. Then the Priest says the Collect proper to the day, at the end of which the people acclaim:' },
      { speaker: 'people', text: 'Amen.', isResponse: true },
    ],
  },
];

export const LITURGY_OF_WORD: MassSection[] = [
  {
    id: 'first-reading',
    title: 'First Reading',
    parts: [
      { speaker: 'rubric', text: 'The reader goes to the ambo and reads the First Reading, which all listen to while seated.' },
      { speaker: 'rubric', text: 'At the end of the reading:' },
      { speaker: 'lector', text: 'The word of the Lord.' },
      { speaker: 'people', text: 'Thanks be to God.', isResponse: true },
    ],
  },
  {
    id: 'responsorial-psalm',
    title: 'Responsorial Psalm',
    parts: [
      { speaker: 'rubric', text: 'The psalmist or cantor sings or says the Psalm, with the people making the response.' },
    ],
  },
  {
    id: 'second-reading',
    title: 'Second Reading',
    rubric: 'On Sundays and Solemnities, there is a Second Reading.',
    parts: [
      { speaker: 'lector', text: 'The word of the Lord.' },
      { speaker: 'people', text: 'Thanks be to God.', isResponse: true },
    ],
  },
  {
    id: 'gospel-acclamation',
    title: 'Gospel Acclamation',
    parts: [
      { speaker: 'rubric', text: 'All stand. The Alleluia or other chant is sung. During Lent, a verse before the Gospel is used instead of the Alleluia.' },
    ],
  },
  {
    id: 'gospel',
    title: 'Gospel',
    parts: [
      { speaker: 'deacon', text: 'The Lord be with you.' },
      { speaker: 'people', text: 'And with your spirit.', isResponse: true },
      { speaker: 'deacon', text: 'A reading from the holy Gospel according to N.' },
      { speaker: 'people', text: 'Glory to you, O Lord.', isResponse: true },
      { speaker: 'rubric', text: 'At the end of the Gospel:' },
      { speaker: 'deacon', text: 'The Gospel of the Lord.' },
      { speaker: 'people', text: 'Praise to you, Lord Jesus Christ.', isResponse: true },
    ],
  },
  {
    id: 'homily',
    title: 'Homily',
    parts: [
      { speaker: 'rubric', text: 'Then the Homily is given by the Priest or Deacon.' },
    ],
  },
  {
    id: 'creed',
    title: 'Profession of Faith (Niceno-Constantinopolitan Creed)',
    parts: [
      {
        speaker: 'all',
        text: 'I believe in one God,\nthe Father almighty,\nmaker of heaven and earth,\nof all things visible and invisible.\n\nI believe in one Lord Jesus Christ,\nthe Only Begotten Son of God,\nborn of the Father before all ages.\nGod from God, Light from Light,\ntrue God from true God,\nbegotten, not made, consubstantial with the Father;\nthrough him all things were made.\nFor us men and for our salvation\nhe came down from heaven,\nand by the Holy Spirit was incarnate of the Virgin Mary,\nand became man.\n\nFor our sake he was crucified under Pontius Pilate,\nhe suffered death and was buried,\nand rose again on the third day\nin accordance with the Scriptures.\nHe ascended into heaven\nand is seated at the right hand of the Father.\nHe will come again in glory\nto judge the living and the dead\nand his kingdom will have no end.\n\nI believe in the Holy Spirit, the Lord, the giver of life,\nwho proceeds from the Father and the Son,\nwho with the Father and the Son is adored and glorified,\nwho has spoken through the prophets.\nI believe in one, holy, catholic and apostolic Church.\nI confess one Baptism for the forgiveness of sins\nand I look forward to the resurrection of the dead\nand the life of the world to come. Amen.',
        latin: 'Credo in unum Deum,\nPatrem omnipoténtem,\nfactórem cæli et terræ,\nvisibílium ómnium et invisibílium.\n\nEt in unum Dóminum Iesum Christum,\nFílium Dei Unigénitum,\net ex Patre natum ante ómnia sǽcula.\nDeum de Deo, lumen de lúmine,\nDeum verum de Deo vero,\ngénitum, non factum, consubstantiálem Patri:\nper quem ómnia facta sunt.\nQui propter nos hómines et propter nostram salútem\ndescéndit de cælis.\nEt incarnátus est de Spíritu Sancto\nex María Vírgine, et homo factus est.\n\nCrucifíxus étiam pro nobis sub Póntio Piláto;\npassus et sepúltus est,\net resurréxit tértia die,\nsecúndum Scriptúras,\net ascéndit in cælum,\nsedet ad déxteram Patris.\nEt íterum ventúrus est cum glória,\niudicáre vivos et mórtuos,\ncuius regni non erit finis.\n\nEt in Spíritum Sanctum, Dóminum et vivificántem:\nqui ex Patre Filióque procédit.\nQui cum Patre et Fílio simul adorátur et conglorificátur:\nqui locútus est per prophétas.\nEt unam, sanctam, cathólicam et apostólicam Ecclésiam.\nConfíteor unum baptísma in remissiónem peccatórum.\nEt expécto resurrectiónem mortuórum,\net vitam ventúri sǽculi. Amen.',
      },
    ],
  },
  {
    id: 'prayer-of-faithful',
    title: 'Prayer of the Faithful (Universal Prayer)',
    parts: [
      { speaker: 'rubric', text: 'The Priest invites the people to pray. The intentions are announced, and the people respond.' },
    ],
  },
];

export const LITURGY_OF_EUCHARIST: MassSection[] = [
  {
    id: 'preparation-gifts',
    title: 'Preparation of the Gifts',
    parts: [
      { speaker: 'priest', text: 'Blessed are you, Lord God of all creation,\nfor through your goodness we have received\nthe bread we offer you:\nfruit of the earth and work of human hands,\nit will become for us the bread of life.' },
      { speaker: 'people', text: 'Blessed be God for ever.', isResponse: true },
      { speaker: 'priest', text: 'Blessed are you, Lord God of all creation,\nfor through your goodness we have received\nthe wine we offer you:\nfruit of the vine and work of human hands,\nit will become our spiritual drink.' },
      { speaker: 'people', text: 'Blessed be God for ever.', isResponse: true },
      { speaker: 'priest', text: 'Pray, brethren (brothers and sisters),\nthat my sacrifice and yours\nmay be acceptable to God,\nthe almighty Father.' },
      { speaker: 'people', text: 'May the Lord accept the sacrifice at your hands\nfor the praise and glory of his name,\nfor our good\nand the good of all his holy Church.', isResponse: true },
    ],
  },
  {
    id: 'prayer-over-offerings',
    title: 'Prayer over the Offerings',
    parts: [
      { speaker: 'rubric', text: 'The Priest says the Prayer over the Offerings proper to the day, at the end of which the people acclaim:' },
      { speaker: 'people', text: 'Amen.', isResponse: true },
    ],
  },
  {
    id: 'preface-dialogue',
    title: 'Preface Dialogue',
    parts: [
      { speaker: 'priest', text: 'The Lord be with you.' },
      { speaker: 'people', text: 'And with your spirit.', isResponse: true },
      { speaker: 'priest', text: 'Lift up your hearts.' },
      { speaker: 'people', text: 'We lift them up to the Lord.', isResponse: true },
      { speaker: 'priest', text: 'Let us give thanks to the Lord our God.' },
      { speaker: 'people', text: 'It is right and just.', isResponse: true },
    ],
  },
  {
    id: 'sanctus',
    title: 'Sanctus (Holy, Holy, Holy)',
    parts: [
      {
        speaker: 'all',
        text: 'Holy, Holy, Holy Lord God of hosts.\nHeaven and earth are full of your glory.\nHosanna in the highest.\nBlessed is he who comes in the name of the Lord.\nHosanna in the highest.',
        latin: 'Sanctus, Sanctus, Sanctus Dóminus Deus Sábaoth.\nPleni sunt cæli et terra glória tua.\nHosánna in excélsis.\nBenedíctus qui venit in nómine Dómini.\nHosánna in excélsis.',
      },
    ],
  },
  {
    id: 'mystery-of-faith',
    title: 'Mystery of Faith',
    parts: [
      { speaker: 'priest', text: 'The mystery of faith.' },
      { speaker: 'rubric', text: 'The people respond using one of the following:' },
      { speaker: 'people', text: 'We proclaim your Death, O Lord,\nand profess your Resurrection\nuntil you come again.', isResponse: true },
      { speaker: 'rubric', text: 'Or:' },
      { speaker: 'people', text: 'When we eat this Bread and drink this Cup,\nwe proclaim your Death, O Lord,\nuntil you come again.', isResponse: true },
      { speaker: 'rubric', text: 'Or:' },
      { speaker: 'people', text: 'Save us, Savior of the world,\nfor by your Cross and Resurrection\nyou have set us free.', isResponse: true },
    ],
  },
  {
    id: 'doxology',
    title: 'Doxology',
    parts: [
      { speaker: 'priest', text: 'Through him, and with him, and in him,\nO God, almighty Father,\nin the unity of the Holy Spirit,\nall glory and honor is yours,\nfor ever and ever.' },
      { speaker: 'people', text: 'Amen.', isResponse: true },
    ],
  },
];

export const COMMUNION_RITE: MassSection[] = [
  {
    id: 'lords-prayer',
    title: 'The Lord\'s Prayer',
    parts: [
      { speaker: 'priest', text: 'At the Savior\'s command\nand formed by divine teaching,\nwe dare to say:' },
      {
        speaker: 'all',
        text: 'Our Father, who art in heaven,\nhallowed be thy name;\nthy kingdom come,\nthy will be done\non earth as it is in heaven.\nGive us this day our daily bread,\nand forgive us our trespasses,\nas we forgive those who trespass against us;\nand lead us not into temptation,\nbut deliver us from evil.',
        latin: 'Pater noster, qui es in cælis:\nsanctificétur nomen tuum;\nadvéniat regnum tuum;\nfiat volúntas tua,\nsicut in cælo, et in terra.\nPanem nostrum cotidiánum da nobis hódie;\net dimítte nobis débita nostra,\nsicut et nos dimíttimus debitóribus nostris;\net ne nos indúcas in tentatiónem;\nsed líbera nos a malo.',
      },
      { speaker: 'priest', text: 'Deliver us, Lord, we pray, from every evil,\ngraciously grant peace in our days,\nthat, by the help of your mercy,\nwe may be always free from sin\nand safe from all distress,\nas we await the blessed hope\nand the coming of our Savior, Jesus Christ.' },
      { speaker: 'people', text: 'For the kingdom,\nthe power and the glory are yours\nnow and for ever.', isResponse: true },
    ],
  },
  {
    id: 'sign-of-peace',
    title: 'Sign of Peace',
    parts: [
      { speaker: 'priest', text: 'Lord Jesus Christ,\nwho said to your Apostles:\nPeace I leave you, my peace I give you,\nlook not on our sins,\nbut on the faith of your Church,\nand graciously grant her peace and unity\nin accordance with your will.\nWho live and reign for ever and ever.' },
      { speaker: 'people', text: 'Amen.', isResponse: true },
      { speaker: 'priest', text: 'The peace of the Lord be with you always.' },
      { speaker: 'people', text: 'And with your spirit.', isResponse: true },
      { speaker: 'deacon', text: 'Let us offer each other the sign of peace.' },
    ],
  },
  {
    id: 'agnus-dei',
    title: 'Lamb of God (Agnus Dei)',
    parts: [
      {
        speaker: 'all',
        text: 'Lamb of God, you take away the sins of the world,\n\thave mercy on us.\nLamb of God, you take away the sins of the world,\n\thave mercy on us.\nLamb of God, you take away the sins of the world,\n\tgrant us peace.',
        latin: 'Agnus Dei, qui tollis peccáta mundi:\n\tmiserére nobis.\nAgnus Dei, qui tollis peccáta mundi:\n\tmiserére nobis.\nAgnus Dei, qui tollis peccáta mundi:\n\tdona nobis pacem.',
      },
    ],
  },
  {
    id: 'communion',
    title: 'Communion',
    parts: [
      { speaker: 'rubric', text: 'The Priest genuflects, takes the host and, holding it slightly raised above the paten or above the chalice, while facing the people, says:' },
      { speaker: 'priest', text: 'Behold the Lamb of God,\nbehold him who takes away the sins of the world.\nBlessed are those called to the supper of the Lamb.' },
      { speaker: 'all', text: 'Lord, I am not worthy\nthat you should enter under my roof,\nbut only say the word\nand my soul shall be healed.' },
      { speaker: 'rubric', text: 'The minister of Communion says:' },
      { speaker: 'priest', text: 'The Body of Christ.' },
      { speaker: 'people', text: 'Amen.', isResponse: true },
      { speaker: 'priest', text: 'The Blood of Christ.' },
      { speaker: 'people', text: 'Amen.', isResponse: true },
    ],
  },
  {
    id: 'prayer-after-communion',
    title: 'Prayer after Communion',
    parts: [
      { speaker: 'rubric', text: 'The Priest says the Prayer after Communion proper to the day, at the end of which the people acclaim:' },
      { speaker: 'people', text: 'Amen.', isResponse: true },
    ],
  },
];

export const CONCLUDING_RITES: MassSection[] = [
  {
    id: 'final-blessing',
    title: 'Final Blessing',
    parts: [
      { speaker: 'priest', text: 'The Lord be with you.' },
      { speaker: 'people', text: 'And with your spirit.', isResponse: true },
      { speaker: 'priest', text: 'May almighty God bless you,\nthe Father, and the Son, ✠ and the Holy Spirit.' },
      { speaker: 'people', text: 'Amen.', isResponse: true },
    ],
  },
  {
    id: 'dismissal',
    title: 'Dismissal',
    parts: [
      { speaker: 'rubric', text: 'The Deacon, or the Priest, says one of the following:' },
      { speaker: 'deacon', text: 'Go forth, the Mass is ended.' },
      { speaker: 'people', text: 'Thanks be to God.', isResponse: true },
      { speaker: 'rubric', text: 'Or:' },
      { speaker: 'deacon', text: 'Go and announce the Gospel of the Lord.' },
      { speaker: 'people', text: 'Thanks be to God.', isResponse: true },
      { speaker: 'rubric', text: 'Or:' },
      { speaker: 'deacon', text: 'Go in peace, glorifying the Lord by your life.' },
      { speaker: 'people', text: 'Thanks be to God.', isResponse: true },
      { speaker: 'rubric', text: 'Or:' },
      { speaker: 'deacon', text: 'Go in peace.' },
      { speaker: 'people', text: 'Thanks be to God.', isResponse: true },
    ],
  },
];

/** All sections in order */
export const FULL_ORDER_OF_MASS = [
  { group: 'The Introductory Rites', sections: INTRODUCTORY_RITES },
  { group: 'The Liturgy of the Word', sections: LITURGY_OF_WORD },
  { group: 'The Liturgy of the Eucharist', sections: LITURGY_OF_EUCHARIST },
  { group: 'The Communion Rite', sections: COMMUNION_RITE },
  { group: 'The Concluding Rites', sections: CONCLUDING_RITES },
];

/** ICEL copyright notice - required for display */
export const ICEL_COPYRIGHT = 'Excerpts from the English translation of The Roman Missal © 2010, International Commission on English in the Liturgy Corporation (ICEL). All rights reserved.';

/** USCCB readings copyright notice */
export const USCCB_COPYRIGHT = 'Lectionary for Mass for Use in the Dioceses of the United States, second typical edition, Copyright © 2001, 1998, 1997, 1986, 1970 Confraternity of Christian Doctrine; Psalm refrain © 1968, 1981, 1997, International Committee on English in the Liturgy, Inc. All rights reserved.';
